import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { IntensityKind, UnitsPreference } from "@heraion/shared";

import { Chip } from "../../primitives/Chip";
import type { UiTheme } from "../../theme";
import { useKindLabels } from "../useKindLabels";
import { makeLoadAxisSelectorStyles } from "./loadAxisSelectorStyles";

// The four prescription commands this selector offers (mockup §01): the load
// axis is mutually exclusive (SetIntensity is a discriminated union), so one
// radiogroup, not four independent toggles. `none` and `rpe` exist on
// IntensityKind but are not offered here: `none` is the unset state (no chip
// selected until the coach picks one) and `rpe` is reached the same way
// `rir` is — by tapping the effort band, which K2.2's applyPrescribeEffort
// already owns; this selector only switches the AXIS to "sforzo" (rir), not
// the RPE/RIR display preference.
const AXIS_KINDS: readonly IntensityKind[] = [
  "absolute",
  "percent-of-max",
  "history",
  "rir",
];

export type LoadAxisSelectorProps = {
  theme: UiTheme;
  /** The bound set's current axis (DraftSet.intensityPrescribed?.kind ??
   *  'none') — 'none' selects nothing, matching a set nobody has prescribed
   *  yet. */
  kind: IntensityKind;
  onSelect: (kind: IntensityKind) => void;
  /** The unit the DRAFT cells are expressed in (composer.cellUnit) — passed
   *  straight through to useKindLabels so the "kg"/"LB" chip label matches
   *  what the metric table and day-panel already show.
   *  ⛔ Required (CN-26 wave 3): see useKindLabels. */
  cellUnit: UnitsPreference;
};

// PE-22 K3.2 — «Cosa determina il carico»: prescribe-only. Clones the
// radiogroup-of-Chip pattern at ComposerSetRow.tsx:216-247 (the BOUNDS work-
// axis selector) rather than inventing a SegmentedControl or reaching for the
// Select dropdown — the consolidation scout's call.
//
// Review fix (PE-22 K3 triad, Critical): the four axis labels used to live
// here as their own `mobile.logger.loadAxis*` keys, duplicating the
// intensity-kind label resolver useKindLabels.ts already owns as "the ONE
// place" (see its own header — the PE-14 English-leak bug came from exactly
// this kind of bypass). Routing through useKindLabels here closes that gap.
export function LoadAxisSelector({
  theme,
  kind,
  onSelect,
  cellUnit,
}: LoadAxisSelectorProps) {
  const { t } = useTranslation();
  const styles = makeLoadAxisSelectorStyles(theme);
  const kindLabels = useKindLabels(cellUnit);
  const label = t("mobile.logger.loadAxisTitle", {
    defaultValue: "What determines the load",
  });

  return (
    <View style={styles.line}>
      <Text style={styles.label}>{label}</Text>
      <View
        accessibilityRole="radiogroup"
        accessibilityLabel={label}
        style={styles.row}
      >
        {AXIS_KINDS.map((axisKind) => (
          <Chip
            key={axisKind}
            theme={theme}
            role="radio"
            label={kindLabels[axisKind]}
            selected={kind === axisKind}
            onPress={() => onSelect(axisKind)}
          />
        ))}
      </View>
    </View>
  );
}
